// The template engine, which evaluates nothing.
//
// WHY THIS EXISTS
//
// It replaces `ejs.compile(<tenant string>)(locals)` at the four sites that
// compile tenant-authored content: a section's `html` and `style`, an element's
// `frame`, and a site's or page's `bodyTemplate`. EJS templates are JavaScript
// — `ejs.compile` builds a function with `new Function` and runs it — so those
// four fields were arbitrary code, in two places at once:
//
//   * inside the render Lambda, on an ANONYMOUS GET (`api/code/src/index.js`
//     mounts `/site/*` outside the session middleware), where the compiled
//     function could read `process.env` and reach the execution role; and
//   * inside the app's own window, because `components/preview/composed.js`
//     calls `render()` synchronously and only then hands the string to a
//     sandboxed `srcDoc` — the sandbox contains the emitted `<script>`, never
//     the compile.
//
// Containment could not fix that. A credential-free compose Lambda closes the
// first and does nothing about the second, and `node:vm` is not a boundary in
// either (`this.constructor.constructor` walks out of it) and does not exist in
// a browser at all — which would mean two implementations of the composer, the
// exact drift `common/render` was created to prevent. So the sink is removed
// rather than relocated: this module parses the same syntax and interprets a
// tree, and there is no `new Function`, no `eval` and no way to call anything.
//
// WHAT IT SUPPORTS, AND WHY EXACTLY THAT
//
// Not a guess. Every template stored on dev was read — 1290 strings across
// sections, page elements and site configuration — and the grammar below is
// what they use, plus nothing:
//
//     2783  <%- %>      raw output          25 distinct expressions in total
//       47  <%= %>      escaped output       0 function calls anywhere
//       10  <%  %>      control flow         0 include(), 0 <%_, 0 -%>, 0 <%%
//
// 2778 of those occurrences are `content`, `header` and `footer` — the shell's
// own defaults. Three strings in the whole estate use anything beyond a plain
// property path, and they belong to two owners: a section named `navbar` and
// one page element. Between them they need `if`, nested `for…of`, a ternary,
// `.length`, a `>` comparison, and one `const` bound to a template literal.
//
// **There are no function calls, so there is no call syntax.** That is the
// single most important line in this file: a call is where the next escape
// lives, and nothing in the estate needs one. `.forEach(function (x) {` is
// recognised as a LOOP FORM by the statement parser — a shape, not an
// expression being invoked — because it is the other spelling a person copying
// an EJS tutorial writes.
//
// The gen-2 estate, imported later, is what the grammar grew for since: a
// destructured binding and a `forEach` index, `for … in`, a `/* */` comment
// inside `<% %>`, and array and object literals. Each is a parser addition —
// walking keys, binding by shape, skipping a comment and constructing a value
// are none of them a call — and `docs/gen2-gaps.md` is the scoreboard.
//
// Errors are thrown, not swallowed. Every caller already renders an exception
// as content in place, which is the composer's rule: a bad template shows a
// message where the block would have been and the rest of the page still
// serves.

// EJS's own escape, character for character, including which characters it does
// NOT touch: the backtick and `/` are left alone, and `"` and `'` become
// numeric references rather than named ones. Pinned against `ejs.min.js` rather
// than assumed, because the compatibility corpus asserts byte-identical output
// against the HTML gen-2 produced and a different entity spelling would fail it
// for no behavioural reason.
export const escapeHtml = (value) =>
  value === undefined || value === null
    ? ""
    : String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&#34;")
        .replace(/'/g, "&#39;");

// Bounds. A tenant's own page being expensive to compose is their cache key and
// their problem, but it must not be able to hang the render Lambda or the
// editor's tab, and an interpreter makes that cheap to enforce where a compiled
// function did not.
const MAX_ITERATIONS = 10000;
const MAX_OUTPUT = 1024 * 1024;
const MAX_DEPTH = 32;

class TemplateError extends Error {}

const fail = (message) => {
  throw new TemplateError(message);
};

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

// `<%`, an optional sigil, everything up to the first `%>`. Non-greedy, so a
// template with several tags does not swallow the text between them.
//
// EJS's whitespace-control spellings (`<%_`, `-%>`, `_%>`) and its literal
// escape (`<%%`) are deliberately absent: the estate uses none of them, and a
// tag this does not recognise is better as a visible parse error than as
// silently different output.
const TAG = /<%([-=#]?)([\s\S]*?)%>/g;

const tokenize = (source) => {
  const tokens = [];
  let last = 0;
  let match;

  TAG.lastIndex = 0;

  while ((match = TAG.exec(source)) !== null) {
    if (match.index > last) {
      tokens.push({ type: "text", value: source.slice(last, match.index) });
    }

    const sigil = match[1];
    const body = match[2];

    if (sigil === "#") {
      // A comment contributes nothing, not even whitespace.
    } else if (sigil === "=" || sigil === "-") {
      tokens.push({ type: "output", escaped: sigil === "=", source: body });
    } else {
      tokens.push({ type: "code", source: body });
    }

    last = match.index + match[0].length;
  }

  if (last < source.length) {
    tokens.push({ type: "text", value: source.slice(last) });
  }

  // An unclosed `<%` leaves the rest of the template as text, which renders the
  // tag's source into the page and reads as nonsense. Saying so is better.
  const opens = (source.match(/<%/g) || []).length;
  const closes = (source.match(/%>/g) || []).length;
  if (opens !== closes) {
    fail(`unclosed tag: ${opens} '<%' and ${closes} '%>'`);
  }

  return tokens;
};

// ---------------------------------------------------------------------------
// Expression lexer
// ---------------------------------------------------------------------------

const PUNCT = [
  "===",
  "!==",
  "&&",
  "||",
  "...",
  "==",
  "!=",
  "<=",
  ">=",
  "?",
  ":",
  ".",
  ",",
  "(",
  ")",
  "[",
  "]",
  "{",
  "}",
  "<",
  ">",
  "!",
];

const isIdentStart = (c) => /[A-Za-z_$]/.test(c);
const isIdentPart = (c) => /[A-Za-z0-9_$]/.test(c);

// A `/* … */` comment, from its `/*` at `start`, to the index of its closing
// `/`. gen-2's sections open with a commented-out `/*console.log(values);*/`
// in fourteen blocks, and that comment carries a `;` and a call — so it is
// skipped as a whole before anything else reads it, never parsed and then
// ignored. A comment that is never closed would otherwise swallow the rest of
// the scriptlet, which is a statement silently gone.
const skipComment = (src, start) => {
  const end = src.indexOf("*/", start + 2);
  if (end < 0) fail("unterminated comment: '/*' without '*/'");
  return end + 2;
};

// A template literal is lexed whole, with its `${...}` holes kept as raw source
// for the parser to recurse into. Only one construct in the estate uses one —
// `` `/post/${entry.slug}` `` — but it is a string with interpolation and
// treating it as an opaque token would render the braces literally.
const lexTemplate = (src, start) => {
  let i = start + 1;
  const parts = [];
  let text = "";

  while (i < src.length && src[i] !== "`") {
    if (src[i] === "\\") {
      text += src[i + 1];
      i += 2;
      continue;
    }

    if (src[i] === "$" && src[i + 1] === "{") {
      parts.push({ text });
      text = "";
      let depth = 1;
      let j = i + 2;
      while (j < src.length && depth > 0) {
        if (src[j] === "{") depth += 1;
        else if (src[j] === "}") depth -= 1;
        if (depth > 0) j += 1;
      }
      if (depth !== 0) fail("unterminated ${ in template literal");
      parts.push({ expr: src.slice(i + 2, j) });
      i = j + 1;
      continue;
    }

    text += src[i];
    i += 1;
  }

  if (i >= src.length) fail("unterminated template literal");

  parts.push({ text });
  return { token: { type: "template", parts }, next: i + 1 };
};

const lex = (src) => {
  const out = [];
  let i = 0;

  while (i < src.length) {
    const c = src[i];

    if (/\s/.test(c)) {
      i += 1;
      continue;
    }

    // A `/* */` comment is whitespace, here as in the statement splitter —
    // see `skipComment`.
    if (c === "/" && src[i + 1] === "*") {
      i = skipComment(src, i);
      continue;
    }

    if (c === "'" || c === '"') {
      let j = i + 1;
      let value = "";
      while (j < src.length && src[j] !== c) {
        if (src[j] === "\\") {
          value += src[j + 1];
          j += 2;
          continue;
        }
        value += src[j];
        j += 1;
      }
      if (j >= src.length) fail("unterminated string");
      out.push({ type: "string", value });
      i = j + 1;
      continue;
    }

    if (c === "`") {
      const { token, next } = lexTemplate(src, i);
      out.push(token);
      i = next;
      continue;
    }

    if (/[0-9]/.test(c)) {
      let j = i;
      while (j < src.length && /[0-9.]/.test(src[j])) j += 1;
      out.push({ type: "number", value: Number(src.slice(i, j)) });
      i = j;
      continue;
    }

    if (isIdentStart(c)) {
      let j = i;
      while (j < src.length && isIdentPart(src[j])) j += 1;
      out.push({ type: "name", value: src.slice(i, j) });
      i = j;
      continue;
    }

    const punct = PUNCT.find((p) => src.startsWith(p, i));
    if (punct) {
      out.push({ type: "punct", value: punct });
      i += punct.length;
      continue;
    }

    // `+` is the one of these the estate actually writes — `"ctrl-"+idx` —
    // so it is refused with the spelling that does work rather than with its
    // own source quoted back.
    if (c === "+") {
      fail("'+' is not supported: a template does no arithmetic or concatenation — build a string with a template literal, `ctrl-${idx}`");
    }

    // `=` on its own, `;`, `+` — anything the grammar has no place for.
    // Refusing loudly is the point: a template doing arithmetic or assigning
    // inside an expression is a template written against EJS's semantics, and
    // rendering it as if it worked would be worse than saying this engine does
    // not do that.
    fail(`unsupported syntax at '${src.slice(i, i + 12).trim()}'`);
  }

  return out;
};

// ---------------------------------------------------------------------------
// Expression parser — precedence climbing, no calls
// ---------------------------------------------------------------------------

const parseExpression = (tokens) => {
  let pos = 0;

  const peek = () => tokens[pos];
  const at = (value) => peek() && peek().type === "punct" && peek().value === value;
  const eat = (value) => {
    if (!at(value)) fail(`expected '${value}'`);
    pos += 1;
  };

  const parsePrimary = () => {
    const t = peek();
    if (!t) fail("unexpected end of expression");

    if (t.type === "string") {
      pos += 1;
      return { k: "lit", v: t.value };
    }

    if (t.type === "number") {
      pos += 1;
      return { k: "lit", v: t.value };
    }

    if (t.type === "template") {
      pos += 1;
      return {
        k: "tmpl",
        parts: t.parts.map((p) =>
          "expr" in p ? { expr: compile(p.expr) } : { text: p.text }
        ),
      };
    }

    if (at("(")) {
      eat("(");
      const inner = parseTernary();
      eat(")");
      return inner;
    }

    if (at("[")) return parseArray();
    if (at("{")) return parseObject();

    if (at("!")) {
      pos += 1;
      return { k: "not", e: parseUnary() };
    }

    if (t.type === "name") {
      pos += 1;
      if (t.value === "true") return { k: "lit", v: true };
      if (t.value === "false") return { k: "lit", v: false };
      if (t.value === "null") return { k: "lit", v: null };
      if (t.value === "undefined") return { k: "lit", v: undefined };
      return { k: "name", n: t.value };
    }

    fail(`unexpected token '${t.value}'`);
  };

  // A list and an object CONSTRUCT a value; neither runs anything. The estate
  // writes both for one reason — to name data the markup below then reads,
  // `const data = { left : values.tiers[0], … }` and an array of thirty
  // `{fileid, alt}` — so what is modelled is exactly that: values separated by
  // commas, `key: value` pairs, a trailing comma. Everything else a literal can
  // carry is refused by name, the way a destructuring pattern refuses what it
  // does not model: a spread, a hole, a computed key, a shorthand key and a
  // method.
  const parseArray = () => {
    eat("[");
    const items = [];
    while (!at("]")) {
      if (at(",")) fail("a hole in an array literal");
      if (at("...")) fail("spread in an array literal");
      items.push(parseTernary());
      if (!at("]")) eat(",");
    }
    eat("]");
    return { k: "arr", items };
  };

  const parseObject = () => {
    eat("{");
    const props = [];
    while (!at("}")) {
      if (at("...")) fail("spread in an object literal");
      if (at("[")) fail("a computed key in an object literal");
      const t = peek();
      if (!t || !["name", "string", "number"].includes(t.type)) {
        fail(`unexpected '${t ? t.value : "end"}' in an object literal`);
      }
      pos += 1;
      const key = String(t.value);
      if (at(",") || at("}")) fail(`a shorthand key in an object literal ('${key}')`);
      if (at("(")) fail(`a method in an object literal ('${key}')`);
      // `{__proto__: x}` is the one key that ASSIGNS a prototype rather than
      // naming a property, and the other two are what the member read refuses
      // to hand out. Refused at parse, so the object built below is plain.
      if (key === "__proto__" || key === "constructor" || key === "prototype") {
        fail(`'${key}' is not a key a template can write`);
      }
      eat(":");
      props.push({ key, v: parseTernary() });
      if (!at("}")) eat(",");
    }
    eat("}");
    return { k: "obj", props };
  };

  // `.prop` and `[expr]` only. There is no `(` case here and that absence is
  // the security property: a call cannot be parsed, so it cannot be evaluated.
  const parseMember = () => {
    let node = parsePrimary();

    for (;;) {
      if (at(".")) {
        eat(".");
        const name = peek();
        if (!name || name.type !== "name") fail("expected a property name after '.'");
        pos += 1;
        node = { k: "member", o: node, p: { k: "lit", v: name.value } };
        continue;
      }

      if (at("[")) {
        eat("[");
        const idx = parseTernary();
        eat("]");
        node = { k: "member", o: node, p: idx };
        continue;
      }

      if (at("(")) {
        fail("function calls are not supported in a template expression");
      }

      return node;
    }
  };

  const parseUnary = () => (at("!") ? (pos++, { k: "not", e: parseUnary() }) : parseMember());

  const BINARY = [
    ["||"],
    ["&&"],
    ["===", "!==", "==", "!="],
    ["<", ">", "<=", ">="],
  ];

  const parseBinary = (level) => {
    if (level >= BINARY.length) return parseUnary();

    let left = parseBinary(level + 1);

    for (;;) {
      const t = peek();
      if (!t || t.type !== "punct" || !BINARY[level].includes(t.value)) return left;
      pos += 1;
      left = { k: "bin", op: t.value, l: left, r: parseBinary(level + 1) };
    }
  };

  const parseTernary = () => {
    const cond = parseBinary(0);
    if (!at("?")) return cond;
    eat("?");
    const yes = parseTernary();
    eat(":");
    const no = parseTernary();
    return { k: "cond", c: cond, y: yes, n: no };
  };

  const node = parseTernary();
  if (pos !== tokens.length) fail(`unexpected trailing '${tokens[pos].value}'`);
  return node;
};

const compile = (source) => {
  const trimmed = String(source).trim();
  if (!trimmed) fail("empty expression");
  return parseExpression(lex(trimmed));
};

// ---------------------------------------------------------------------------
// Evaluation
// ---------------------------------------------------------------------------

// Scope is a chain of plain objects created with a null prototype, so a loop
// variable named `constructor` or `__proto__` is a key and never a way up to
// anything. Locals are the root frame.
const scopeOf = (parent, bindings) => ({ parent, vars: bindings || Object.create(null) });

const lookup = (scope, name) => {
  for (let s = scope; s; s = s.parent) {
    if (Object.prototype.hasOwnProperty.call(s.vars, name)) return s.vars[name];
  }
  // EJS compiles with `with(locals)`, so an unknown identifier is a
  // ReferenceError there and an error in place here. Matching it matters: a
  // template naming a local that does not exist is a mistake worth showing,
  // and rendering it as empty would hide the typo.
  fail(`'${name}' is not defined`);
};

const evaluate = (node, scope, depth) => {
  if (depth > MAX_DEPTH) fail("expression nested too deeply");

  switch (node.k) {
    case "lit":
      return node.v;

    case "name":
      return lookup(scope, node.n);

    case "member": {
      const object = evaluate(node.o, scope, depth + 1);
      // `a.b` where `a` is undefined throws in EJS too — the difference between
      // a value that is absent and a path that is wrong.
      if (object === undefined || object === null) {
        fail("cannot read a property of undefined");
      }
      const key = String(evaluate(node.p, scope, depth + 1));
      // A template must not be able to reach a prototype: `values.constructor`
      // is how you get from data to a function in JavaScript, and data is all
      // this is meant to read.
      if (key === "__proto__" || key === "constructor" || key === "prototype") {
        fail(`'${key}' is not readable from a template`);
      }
      const value = object[key];
      return typeof value === "function" ? fail(`'${key}' is not readable from a template`) : value;
    }

    case "tmpl":
      return node.parts
        .map((p) => ("text" in p ? p.text : stringify(evaluate(p.expr, scope, depth + 1))))
        .join("");

    case "arr":
      return node.items.map((item) => evaluate(item, scope, depth + 1));

    case "obj": {
      // A plain object, so it prints as `[object Object]` the way EJS printed
      // one. Safe to assign into: the three keys that would reach a prototype
      // never got past the parser.
      const object = {};
      for (const prop of node.props) object[prop.key] = evaluate(prop.v, scope, depth + 1);
      return object;
    }

    case "not":
      return !evaluate(node.e, scope, depth + 1);

    case "cond":
      return evaluate(node.c, scope, depth + 1)
        ? evaluate(node.y, scope, depth + 1)
        : evaluate(node.n, scope, depth + 1);

    case "bin": {
      const l = evaluate(node.l, scope, depth + 1);
      // Short-circuit, like the language the syntax is borrowed from.
      if (node.op === "&&") return l ? evaluate(node.r, scope, depth + 1) : l;
      if (node.op === "||") return l ? l : evaluate(node.r, scope, depth + 1);

      const r = evaluate(node.r, scope, depth + 1);
      switch (node.op) {
        case "===":
          return l === r;
        case "!==":
          return l !== r;
        case "==":
          return l == r;
        case "!=":
          return l != r;
        case "<":
          return l < r;
        case ">":
          return l > r;
        case "<=":
          return l <= r;
        case ">=":
          return l >= r;
        default:
          return fail(`unsupported operator '${node.op}'`);
      }
    }

    default:
      return fail(`unsupported expression`);
  }
};

// What EJS's `__append` does with a value, so `<%- %>` of a number or an array
// reads the same as it did.
const stringify = (value) =>
  value === undefined || value === null ? "" : String(value);

// ---------------------------------------------------------------------------
// Statement parser
// ---------------------------------------------------------------------------

// One scriptlet can carry several statements — the estate has
// `<% for (const entry of values.collection) { const route = `...` %>` — so a
// `<% %>` is split rather than matched whole.
const IF_OPEN = /^if\s*\(([\s\S]+)\)\s*\{$/;
const ELSE_IF = /^\}\s*else\s+if\s*\(([\s\S]+)\)\s*\{$/;
const ELSE_OPEN = /^\}\s*else\s*\{$/;
const CLOSE = /^\}$/;
// A BINDING is a name or a shorthand object pattern, and it is one concept in
// three statements: `for (const x of …)`, `…forEach(function (x, i) {` and
// `const x = …`. All three bound a single plain identifier and nothing else,
// which refused `{id, route, label}` in every one of them.
//
// Shorthand keys ONLY. Renaming, defaults, nesting, rest and array patterns are
// each refused by name in `bindingOf` rather than half-supported: the corpus
// uses none of them, and a pattern that binds something other than what it
// reads is the kind of thing that should stop rather than guess.
const IDENT = "[A-Za-z_$][\\w$]*";
const BINDING = `(?:${IDENT}|\\{[^{}]*\\})`;

const FOR_OF = new RegExp(`^for\\s*\\(\\s*(?:const|let|var)\\s+(${BINDING})\\s+of\\s+([\\s\\S]+?)\\s*\\)\\s*\\{$`);

// `for (const k in obj)` is a SEPARATE statement from `for … of`, not a spelling
// of it: it walks keys where the other walks values. gen-2 reaches for it
// constantly and always the same way — `for(const idx in values.tiers)` and then
// `values.tiers[idx]` on the next line — which is why the binding here is a plain
// name and not a BINDING. A pattern would have to destructure a key, and a key
// is a string; `for (const {a} in x)` is legal JavaScript that binds nothing
// useful, so it falls through to `unsupported statement` with its own text in it
// rather than being half-accepted.
const FOR_IN = new RegExp(`^for\\s*\\(\\s*(?:const|let|var)\\s+(${IDENT})\\s+in\\s+([\\s\\S]+?)\\s*\\)\\s*\\{$`);

// The SECOND parameter is not a nicety. Every `forEach` in the estate passes an
// index — `function ({id, route, label}, idx)`, and equally `function (label,
// idx)` — and the bodies read it: `data-edit="features[<%=idx%>]"`. So a
// destructuring fix that did not also take the index would have fixed nothing
// at all; the two are one change because the corpus never separates them.
const FOREACH = new RegExp(`^([\\s\\S]+?)\\.forEach\\s*\\(\\s*(?:function\\s*)?\\(\\s*(${BINDING})\\s*(?:,\\s*(${IDENT})\\s*)?\\)\\s*(?:=>\\s*)?\\{$`);
const FOREACH_CLOSE = /^\}\s*\)$/;
const CONST = new RegExp(`^(?:const|let|var)\\s+(${BINDING})\\s*=\\s*([\\s\\S]+)$`);

// `{a, b}` -> the names it binds; `x` -> itself. Anything else names itself in
// the failure, because "unsupported statement" pointing at a whole line is what
// made the two halves of this feature look like two unrelated defects.
const bindingOf = (source) => {
  const src = String(source).trim();
  if (!src.startsWith("{")) return { name: src };

  const inner = src.slice(1, -1).trim();
  if (!inner) fail("a destructuring pattern that binds nothing");

  const names = inner.split(",").map((part) => part.trim()).filter((part) => part !== "");
  for (const name of names) {
    if (name.startsWith("...")) fail(`rest in a destructuring pattern ('${name}')`);
    if (name.includes(":")) fail(`renaming in a destructuring pattern ('${name}')`);
    if (name.includes("=")) fail(`a default in a destructuring pattern ('${name}')`);
    if (!new RegExp(`^${IDENT}$`).test(name)) fail(`'${name}' is not a name a pattern can bind`);
  }
  return { names };
};

// Split a scriptlet into statements. `;` and newlines separate, but neither may
// be honoured inside a string, a template literal, a comment or brackets —
// `${a; b}` is not two statements and `[1, 2]` is not two of anything.
const DECLARES = /(?:^|[^\w$])(?:const|let|var)\s*$/;

// What may stand directly before a `{` that is a VALUE rather than a block:
// `= {`, `({`, `[{`, `, {`, `: {`, `? {`, the operand side of `!`, `&&` and
// `||`, and what a loop walks — `in {` and `of [`. A block's `{` follows `)`,
// `=>`, `else` or nothing.
const VALUE_BEFORE = /(?:[=([,:?!&|]|(?:^|[^\w$])(?:in|of))$/;

const CLOSES = { ")": "(", "]": "[", "}": "{" };

const splitStatements = (source) => {
  const out = [];
  let current = "";
  // The brackets this statement has opened and not closed — `(`, `[` and the
  // `{` of an object literal. A STACK rather than a depth, because `}` is two
  // different things: it closes a literal only when a literal is what is open,
  // and otherwise it is a block's closer and belongs to the statement text
  // (`} else {`, `})`).
  let open = [];
  let pattern = 0;
  let quote = null;

  for (let i = 0; i < source.length; i += 1) {
    const c = source[i];

    // Inside a destructuring pattern nothing separates: its commas are part of
    // one binding and its braces are not blocks.
    if (pattern > 0) {
      current += c;
      if (c === "{") pattern += 1;
      else if (c === "}") pattern -= 1;
      continue;
    }

    if (quote) {
      current += c;
      if (c === "\\") {
        current += source[i + 1] || "";
        i += 1;
      } else if (c === quote) {
        quote = null;
      }
      continue;
    }

    // Skipped before anything else looks at it, so the `;` and the parens in
    // `/*console.log(values);*/` neither end a statement nor open a bracket.
    if (c === "/" && source[i + 1] === "*") {
      i = skipComment(source, i) - 1;
      current += " ";
      continue;
    }

    if (c === "'" || c === '"' || c === "`") {
      quote = c;
      current += c;
      continue;
    }

    if (c === "(" || c === "[") open.push(c);
    if (c === ")" || c === "]" || c === "}") {
      if (open.length && open[open.length - 1] === CLOSES[c]) {
        open.pop();
        current += c;
        continue;
      }
    }

    if (!open.length && (c === ";" || c === "\n")) {
      if (current.trim()) out.push(current.trim());
      current = "";
      continue;
    }

    if (c === "{") {
      // A destructuring pattern's `{`: `const {anchor, content} = values.x`
      // opens its brace where a block's would be, and flushing there split it
      // into `const {` and `anchor, content} = …`, which matched no rule —
      // `unsupported statement 'const {'` on four of docs.tablews.com's blocks.
      if (DECLARES.test(current)) {
        current += c;
        pattern = 1;
        continue;
      }

      // An object literal's `{` — `const data = { left : …` — or a pattern in
      // a `forEach(function ({id, label}, idx)` parameter list. Opened like a
      // bracket, so the newline inside a literal that spans lines does not end
      // the statement it belongs to.
      if (VALUE_BEFORE.test(current.trimEnd())) {
        open.push(c);
        current += c;
        continue;
      }

      // Any other `{` ENDS a statement: it is a block opener, and flushing on
      // it is what lets `for (…) { const x = … ` on ONE line parse as the two
      // statements it is.
      //
      // At ANY depth, and the open brackets are forgotten with it. That is
      // P262: `values.items.forEach(function (…) {` opens a paren whose `)` only
      // arrives in a later `<% }) %>`, so the opener's `{` sits one deep. When a
      // flush needed depth 0 the opener and whatever followed it in the same
      // `<% %>` arrived glued together and matched no rule. The opener consumed
      // that paren; the statement after it starts with none open.
      out.push((current + "{").trim());
      current = "";
      open = [];
      continue;
    }

    current += c;
  }

  if (current.trim()) out.push(current.trim());
  return out;
};

// `for (…)` and its `{` may sit on separate lines, which the split above then
// yields as a statement that is exactly `{`. Gluing it back on is cheaper than
// a grammar that tracks the newline.
const rejoinOpeners = (statements) => {
  const out = [];
  for (const s of statements) {
    if (s === "{" && out.length) {
      out[out.length - 1] += " {";
      continue;
    }
    out.push(s);
  }
  return out;
};

const parse = (tokens) => {
  const root = { type: "block", body: [] };
  const stack = [root];
  const top = () => stack[stack.length - 1];

  const push = (node) => top().body.push(node);

  for (const token of tokens) {
    if (token.type === "text") {
      push({ type: "text", value: token.value });
      continue;
    }

    if (token.type === "output") {
      push({
        type: "output",
        escaped: token.escaped,
        expr: compile(token.source),
      });
      continue;
    }

    for (const statement of rejoinOpeners(splitStatements(token.source))) {
      let m;

      if ((m = IF_OPEN.exec(statement))) {
        const node = { type: "if", cond: compile(m[1]), body: [], alt: null };
        push(node);
        stack.push({ type: "block", body: node.body, owner: node });
        continue;
      }

      if ((m = ELSE_IF.exec(statement))) {
        const frame = stack.pop();
        if (!frame || !frame.owner || frame.owner.type !== "if") {
          fail("'else if' without a matching 'if'");
        }
        const node = { type: "if", cond: compile(m[1]), body: [], alt: null };
        frame.owner.alt = [node];
        stack.push({ type: "block", body: node.body, owner: node });
        continue;
      }

      if (ELSE_OPEN.test(statement)) {
        const frame = stack.pop();
        if (!frame || !frame.owner || frame.owner.type !== "if") {
          fail("'else' without a matching 'if'");
        }
        frame.owner.alt = [];
        stack.push({ type: "block", body: frame.owner.alt, owner: frame.owner });
        continue;
      }

      if ((m = FOR_OF.exec(statement))) {
        const node = { type: "for", bind: bindingOf(m[1]), index: null, iter: compile(m[2]), body: [] };
        push(node);
        stack.push({ type: "block", body: node.body, owner: node });
        continue;
      }

      // Same node as `for … of`, carrying a flag rather than a type of its own:
      // everything after the binding — the body, the frame per iteration, the
      // iteration bound, how `}` closes it — is identical, and only what is
      // walked differs. `keys` is what the executor branches on.
      if ((m = FOR_IN.exec(statement))) {
        const node = { type: "for", keys: true, bind: bindingOf(m[1]), index: null, iter: compile(m[2]), body: [] };
        push(node);
        stack.push({ type: "block", body: node.body, owner: node });
        continue;
      }

      if ((m = FOREACH.exec(statement))) {
        const node = { type: "for", bind: bindingOf(m[2]), index: m[3] || null, iter: compile(m[1]), body: [], paren: true };
        push(node);
        stack.push({ type: "block", body: node.body, owner: node });
        continue;
      }

      if (FOREACH_CLOSE.test(statement) || CLOSE.test(statement)) {
        if (stack.length < 2) fail("'}' without a matching block");
        stack.pop();
        continue;
      }

      if ((m = CONST.exec(statement))) {
        push({ type: "let", bind: bindingOf(m[1]), expr: compile(m[2]) });
        continue;
      }

      fail(`unsupported statement '${statement.slice(0, 40)}'`);
    }
  }

  if (stack.length !== 1) fail("a block was opened and never closed");

  return root;
};

// ---------------------------------------------------------------------------
// Execution
// ---------------------------------------------------------------------------

// Put a binding's names into a frame.
//
// A MISSING KEY BINDS `undefined`, and a missing OBJECT fails. That split is
// JavaScript's own and EJS's with it: `const {a} = {}` is fine and `const {a} =
// undefined` throws, so a template that reads a field nobody filled in renders
// empty, and one that destructures a value that is not there says so. It is the
// same argument the `for … of` guard below makes, applied one level down.
const bindInto = (vars, bind, value) => {
  if (bind.name) {
    vars[bind.name] = value;
    return;
  }
  if (value === null || value === undefined) {
    fail(`'{ ${bind.names.join(", ")} }' cannot read ${value === null ? "null" : "undefined"}`);
  }
  for (const name of bind.names) vars[name] = value[name];
};

const run = (block, scope, out) => {
  for (const node of block) {
    if (out.length > MAX_OUTPUT) fail("template produced too much output");

    switch (node.type) {
      case "text":
        out.push(node.value);
        break;

      case "output": {
        const value = evaluate(node.expr, scope, 0);
        out.push(node.escaped ? escapeHtml(value) : stringify(value));
        break;
      }

      case "let":
        // Declared in the CURRENT frame, which for a loop body is the frame
        // created per iteration — so `const route = ...` inside a loop is a
        // fresh binding each time round, as `const` is.
        bindInto(scope.vars, node.bind, evaluate(node.expr, scope, 0));
        break;

      case "if":
        if (evaluate(node.cond, scope, 0)) {
          run(node.body, scopeOf(scope), out);
        } else if (node.alt) {
          run(node.alt, scopeOf(scope), out);
        }
        break;

      case "for": {
        const iterable = evaluate(node.iter, scope, 0);
        let items;

        // A `for…of` over something that is not a list THROWS, including when
        // the value is simply absent — which is what EJS does, and matching it
        // is deliberate rather than incidental.
        //
        // The tempting alternative is to treat undefined as an empty list and
        // render nothing. The compatibility corpus is what argued against it:
        // one real element (`9606858d…`) iterates `values.cards`, and with that
        // value absent EJS raised "not iterable" while an early draft of this
        // rendered a silently empty section. That is an authoring mistake
        // hidden rather than shown, and the composer's rule is the opposite —
        // an error becomes content in place, where the person who can fix it
        // sees it. An empty `[]` still iterates zero times, which is the case
        // that actually means "nothing to show".
        //
        // `for … in` gets its OWN guard because the shapes it accepts are the
        // other's complement: an object is exactly what `for … of` refuses and a
        // list is not what it wants — though an array passes both, since
        // `Object.keys` of one is "0", "1", "2" and that is what the estate
        // iterates. It also gets its own message: "needs a list" is the wrong
        // advice in front of a statement whose whole job is to walk an object.
        // It does NOT copy the paragraph above on an absent value, and the next
        // comment is why.
        //
        // ABSENT IS EMPTY here, and that is the one place the two loops differ
        // on the same question. `for (const k in undefined)` iterates zero times
        // in JavaScript — it does not throw the way `for … of` does — so EJS
        // rendered such a block empty and the gen-2 page looked finished. An
        // engine that fails instead puts a red box where the estate had content,
        // which is the opposite of what carrying these templates across is for.
        //
        // This was the other way round for one release, and the estate is what
        // corrected it: `mvp.pagews.app` publishes a `table` element whose
        // `values` is `{}`, so `for(const hidx in values.headers)` walks
        // undefined. EJS gave an empty table; the strict guard gave
        // `'for … in' needs an object` on a live page.
        //
        // IT WAS MEASURED AND THE MEASUREMENT COULD NOT SEE IT, which is the
        // part worth remembering: `gen2-import check` compiles each block
        // against the values in `plan.json`, and a guard that only fires at
        // RUN time on a value that is absent at RUN time is invisible to it. A
        // parse-time gap the corpus can count; a runtime one it cannot, and
        // this one needed the published rows to show itself.
        //
        // A value that is present and not an object still fails. There is no
        // compatibility case for `for (const k in 5)` — JavaScript walks a
        // number's keys and finds none, quietly — and the estate has no such
        // block, so the loud refusal stays where nothing was relying on silence.
        if (node.keys) {
          if (iterable === null || iterable === undefined) {
            items = [];
          } else if (typeof iterable !== "object") {
            fail("'for … in' needs an object");
          } else {
            // Own enumerable keys, AS STRINGS — Object.keys' order and type,
            // which is what EJS's for-in gave gen-2 and what the bodies index
            // straight back with: `for(const idx in values.tiers)` then
            // `values.tiers[idx]`.
            items = Object.keys(iterable);
          }
        } else {
          if (!Array.isArray(iterable)) {
            fail("'for … of' needs a list");
          }
          items = iterable;
        }

        if (items.length > MAX_ITERATIONS) fail("too many iterations");

        let index = 0;
        for (const item of items) {
          const frame = scopeOf(scope);
          bindInto(frame.vars, node.bind, item);
          if (node.index) frame.vars[node.index] = index;
          index += 1;
          run(node.body, frame, out);
        }
        break;
      }

      default:
        fail("unsupported node");
    }
  }
};

// Render `source` against `locals`.
//
// Throws a TemplateError for anything it cannot parse or evaluate. Callers turn
// that into content in place — see `Template.render`, `Elements.render` and
// `bodyOf`, all of which already did exactly that for EJS's exceptions.
export const render = (source, locals) => {
  if (source === undefined || source === null || source === "") return "";

  const out = [];
  run(parse(tokenize(String(source))).body, scopeOf(null, { ...locals }), out);

  const result = out.join("");
  if (result.length > MAX_OUTPUT) fail("template produced too much output");
  return result;
};

export { TemplateError };
